import { Do, clamp } from '../../universal';
import { $, React, _ } from '../lib';
import { J2rButton } from './component-buttons';
import { J2rComboBox } from './component-combobox';
import { j2h } from './component-j2h';
import { j2r } from './component-react';
import { cacheFileServiceWorkerPromise } from './component-sw-client';
import { moment } from './moment-wrapper';
import { Bindings, ConditionalObject } from './ui5';
export const cachePDFFile = async (hashCode, output, name, cb) => cacheFile(hashCode, output, 'pdf', 'application/pdf', name, cb);
export const cacheXLSXFile = async (hashCode, output, name, cb) => {
    const mime = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
    return cacheFile(hashCode, output, 'xlsx', mime, name, cb);
};
export const cacheCSVFile = async (hashCode, output, name, cb) => cacheFile(hashCode, output, 'csv', 'text/csv', name, cb);
export const cache7ZFile = async (hashCode, output, name, cb) => cacheFile(hashCode, output, '7z', 'application/x-7z-compressed', name, cb);
export const cacheZipFile = async (hashCode, output, name, cb) => cacheFile(hashCode, output, 'zip', 'application/zip', name, cb);
export const cacheFile = async (hashCode, output, extension, mimetype, name, cb) => {
    const tsFriendly = moment().format('YYYYMMDD-HHmmss');
    let basename = `${tsFriendly}.${extension}`;
    if (name != null) {
        basename = `${name}-${basename}`;
    }
    const url_inline = `/rptcache/inline/${hashCode}/${basename}`;
    const url_attach = `/rptcache/attach/${hashCode}/${basename}`;
    const promises = Promise.all([
        cacheFileServiceWorkerPromise(url_inline, output, mimetype, false),
        cacheFileServiceWorkerPromise(url_attach, output, mimetype, true),
    ]);
    return promises
        .then(() => {
        cb(url_inline);
    })
        .catch(err => {
        console.error(err);
    });
};
export class CustomPDFContainer extends React.Component {
    pdfRenderer;
    constructor(props) {
        super(props);
        Bindings(this, [this.resize, this.updatePages]);
        this.pdfRenderer = React.createRef();
        this.state = {
            url: this.props.url ?? null,
            zoom: 'auto',
            zoomText: 'Automatic Zoom',
            showNewComponent: Do(() => {
                const useOld = +(localStorage['loadNativePDFViewer'] ?? 0) || 0;
                return useOld === 0;
            }),
        };
    }
    componentDidUpdate() {
        if (this.state.url !== this.props.url) {
            this.setState({ url: this.props.url ?? null });
        }
    }
    render() {
        return j2r({
            cl: 'pdfjs-container-full',
            children: [
                this.buildToolbar(),
                this.state.showNewComponent
                    ? {
                        tag: CustomPDFRenderer,
                        key: 'pdf',
                        ref: this.pdfRenderer,
                        url: this.state.url,
                        resize: this.resize,
                        updatePages: this.updatePages,
                    }
                    : {
                        tag: 'iframe',
                        key: 'old-pdf',
                        src: this.state.url,
                    },
            ],
        });
    }
    buildToolbar() {
        return {
            key: 'toolbar',
            cl: 'toolbar',
            children: [
                this.state.showNewComponent ? this.buildZoom() : undefined,
                ConditionalObject(!this.state.showNewComponent, {
                    tag: J2rButton,
                    key: 'print',
                    label: '❏ Pop-out',
                    title: 'Opens the PDF in another window - generally so it can be printed',
                    onClick: () => {
                        if (this.state.url != null) {
                            window.open(this.state.url, '_blank');
                        }
                    },
                }),
                {
                    tag: J2rButton,
                    key: 'download',
                    label: '🠇 Download',
                    style: {
                        overflowX: 'hidden',
                        whiteSpace: 'nowrap',
                        textOverflow: 'ellipsis',
                    },
                    title: 'Download a copy of the PDF to your local machine',
                    onClick: () => {
                        if (this.state.url != null) {
                            const url = this.state.url.replace(/inline/, 'attach');
                            window.open(url, '_blank');
                        }
                    },
                },
                {
                    tag: J2rButton,
                    key: 'toggle-view',
                    label: Do(() => {
                        if (this.state.showNewComponent) {
                            return '⎗ Print View';
                        }
                        return '⎗ Display View';
                    }),
                    style: {
                        overflowX: 'hidden',
                        whiteSpace: 'nowrap',
                        textOverflow: 'ellipsis',
                    },
                    title: `Toggles the inline PDF viewer between the built-in browser one and the TriOnline one. Only the built-in browser one can print.`,
                    onClick: () => {
                        const newVal = !this.state.showNewComponent;
                        localStorage['loadNativePDFViewer'] = newVal ? 0 : 1;
                        this.setState({ showNewComponent: newVal });
                    },
                },
                this.state.showNewComponent ? this.buildPages() : undefined,
                ConditionalObject(!this.state.showNewComponent, {
                    key: 'print-msg',
                    cl: 'print-msg',
                    text: 'Use browser PDF viewer to print',
                }),
            ],
        };
    }
    buildZoom() {
        return {
            cl: 'zoom',
            key: 'zoom',
            children: [
                {
                    tag: J2rButton,
                    cl: 'zoom',
                    key: 'zoom-out',
                    label: '‒',
                    title: 'Zooms out - makes the PDF smaller',
                    onClick: () => {
                        this.updateZoom(this.pdfRenderer.current?.changeZoomLevelDelta(-1));
                    },
                },
                {
                    tag: J2rButton,
                    cl: 'zoom',
                    key: 'zoom-in',
                    label: '🞡',
                    title: 'Zooms in - makes the PDF bigger',
                    onClick: () => {
                        this.updateZoom(this.pdfRenderer.current?.changeZoomLevelDelta(+1));
                    },
                },
                {
                    tag: J2rComboBox,
                    key: 'preset-zoom-selector',
                    value: this.state.zoom,
                    textView: () => this.state.zoomText,
                    options: this.zoomOptions().map(x => ({
                        value: x[0],
                        text: x[1],
                    })),
                    onUpdate: (v) => {
                        this.updateZoom(this.pdfRenderer.current?.changeZoomLevel(v.value));
                    },
                },
            ],
        };
    }
    buildPages() {
        return {
            cl: 'pages',
            key: 'pages',
            children: [
                {
                    tag: 'span',
                    key: 'pageCount',
                    text: this.state.page ?? '-',
                },
                {
                    tag: 'span',
                    key: 'separator',
                    cl: 'separator',
                    text: '/',
                },
                {
                    tag: 'span',
                    key: 'pageTotal',
                    text: this.state.pageTotal ?? '-',
                },
            ],
        };
    }
    updatePages(page, pageTotal) {
        if (this.state.pageTotal !== pageTotal) {
            this.setState({ pageTotal });
        }
        if (this.state.page !== page) {
            this.setState({ page });
        }
    }
    resize() {
        const currZoom = this.state.zoom;
        const cmpt = this.pdfRenderer.current;
        cmpt?.changeZoomLevel(1);
        cmpt?.changeZoomLevel(currZoom);
    }
    zoomOptions() {
        return [
            ['auto', 'Automatic Zoom'],
            ['page-actual', 'Actual Size'],
            ['page-fit', 'Page Fit'],
            ['page-width', 'Page Width'],
            ['0.5', '50%'],
            ['0.75', '75%'],
            ['1', '100%'],
            ['1.25', '125%'],
            ['1.5', '150%'],
            ['2', '200%'],
            ['3', '300%'],
            ['4', '400%'],
        ];
    }
    updateZoom(v) {
        if (v == null) {
            return;
        }
        const Z1 = this.zoomOptions();
        const Z2 = _.keyBy(Z1, (x) => x[0]);
        const Z3 = _.mapValues(Z2, (x) => x[1]);
        const title = Do(() => {
            const vStr = String(v);
            if (vStr in Z3) {
                return Z3[vStr];
            }
            else if (isNaN(+v)) {
                return `Unknown: ${v}`;
            }
            return `${Math.round(+v * 100)}%`;
        });
        this.setState({
            zoom: v,
            zoomText: String(title),
        });
    }
}
class CustomPDFRenderer extends React.Component {
    containerparent;
    mounted;
    constructor(props) {
        super(props);
        Bindings(this, [this.updatePagePosition]);
        this.containerparent = React.createRef();
        this.mounted = false;
        this.state = {
            zoom: 'auto',
            pdfViewer: null,
            pdfDocument: null,
        };
    }
    componentDidMount() {
        this.mounted = true;
        const { zoom } = this.state;
        this.changeZoomLevel(1);
        this.changeZoomLevel(zoom);
        this.buildNewReport();
    }
    componentWillUnmount() {
        this.mounted = false;
        if (this.state.pdfViewer && this.state.pdfDocument) {
            this.state.pdfDocument.destroy();
        }
    }
    componentDidUpdate(prevProps, prevState) {
        if (!this.mounted) {
            return;
        }
        if (prevProps.url !== this.props.url) {
            this.setState({ pdfViewer: null }, () => {
                this.buildNewReport();
            });
            return;
        }
        if (prevState.zoom !== this.state.zoom) {
            if (this.state.pdfViewer != null) {
                this.state.pdfViewer.currentScaleValue = this.state.zoom;
            }
            return;
        }
    }
    render() {
        return j2r({
            cl: 'pdfjs-container canselect',
            ref: this.containerparent,
            onScroll: _.debounce(this.updatePagePosition, 200),
        });
    }
    buildNewReport() {
        if (!this.mounted) {
            return;
        }
        if (this.state.pdfDocument == null) {
            this.buildNewReportInner();
            return;
        }
        this.state.pdfDocument.destroy().then(() => {
            this.buildNewReportInner();
        });
    }
    buildNewReportInner() {
        if (!this.props.url || this.containerparent.current == null) {
            return;
        }
        if (!this.mounted) {
            return;
        }
        const $container = $(this.containerparent.current).empty();
        $container.append(j2h({ class: 'pdfjs-container-inner' }));
        const container = $container[0];
        const path = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/2.1.266/pdf.worker.min.js';
        pdfjsLib.GlobalWorkerOptions.workerSrc = path;
        const pdfLinkService = new pdfjsViewer.PDFLinkService();
        const pdfFindController = new pdfjsViewer.PDFFindController({
            linkService: pdfLinkService,
        });
        const pdfViewer = new pdfjsViewer.PDFViewer({
            container,
            linkService: pdfLinkService,
            findController: pdfFindController,
        });
        pdfLinkService.setViewer(pdfViewer);
        pdfViewer.eventBus.on('pagesinit', () => {
            if (this.mounted) {
                this.setState({ pdfViewer }, () => {
                    this.onInit();
                });
            }
        });
        pdfjsLib
            .getDocument(this.props.url)
            .then((pdfDocument) => {
            if (!this.mounted) {
                return;
            }
            this.setState({ pdfDocument });
            this.props.updatePages?.(1, pdfDocument.numPages);
            pdfViewer.setDocument(pdfDocument);
            pdfLinkService.setDocument(pdfDocument, null);
        })
            .catch((err) => {
            console.error(err);
        });
    }
    onInit() {
        if (this.mounted) {
            try {
                this.props.resize?.();
                this.state.pdfViewer.currentScaleValue = this.state.zoom;
            }
            catch (error) { }
        }
    }
    changeZoomLevel(amt) {
        if (this.mounted) {
            amt = amt || this.state.zoom;
            if (!isNaN(+amt)) {
                amt = String(clamp(+amt, 0.1, 4.0));
            }
            this.setState({ zoom: amt });
        }
        return amt;
    }
    changeZoomLevelDelta(delta) {
        const currPercentage = Math.round((this.state.pdfViewer?.currentScale || 1) * 100);
        const modifier = Math.floor(currPercentage / 100) * 10 + 10;
        const new_zoom = Math.round(currPercentage + modifier * delta);
        let new_zoom_rounded = Math.floor(new_zoom / 10) / 10;
        new_zoom_rounded = clamp(new_zoom_rounded, 0.1, 4.0);
        this.setState({ zoom: new_zoom_rounded });
        return new_zoom_rounded;
    }
    updatePagePosition() {
        const pageNumber = this.state.pdfViewer?.currentPageNumber;
        const pageTotal = this.state.pdfDocument?.numPages;
        this.props.updatePages?.(pageNumber || 1, pageTotal || 0);
    }
}
//# sourceMappingURL=component-pdf-viewer.js.map